import { Button } from "@/components/ui/button";
import {
  elfLetterOccasions,
  type ElfLetterOccasion,
  type ElfLetterOccasionId,
} from "@/data/elfLetterOccasions";

interface LetterOccasionPickerProps {
  selected: ElfLetterOccasionId | null;
  pending: ElfLetterOccasion | null;
  disabled: boolean;
  onSelect: (occasion: ElfLetterOccasion) => void;
  onApply: () => void;
  onCancel: () => void;
}

export default function LetterOccasionPicker({
  selected,
  pending,
  disabled,
  onSelect,
  onApply,
  onCancel,
}: LetterOccasionPickerProps) {
  return (
    <fieldset disabled={disabled} className="mb-6 rounded-2xl border border-elfGreen/20 bg-elfGreen-light/25 p-4">
      <legend className="px-1 text-sm font-semibold text-elfGreen-ink">What is your elf writing about?</legend>
      <p className="mb-3 text-xs leading-relaxed text-muted-foreground">Choose a starter, then make the details your own.</p>
      <div className="flex flex-wrap gap-2">
        {elfLetterOccasions.map((occasion) => (
          <Button
            key={occasion.id}
            type="button"
            variant="outline"
            aria-pressed={selected === occasion.id}
            disabled={disabled}
            onClick={() => onSelect(occasion)}
            className={`min-h-11 h-auto whitespace-normal rounded-xl px-3 py-2 text-sm focus-visible:ring-slate-700 ${
              selected === occasion.id
                ? "border-elfGreen-ink bg-elfGreen-ink text-white hover:bg-[#1c432d] hover:text-white"
                : "border-elfGreen/25 bg-white text-elfGreen-ink hover:bg-elfGreen-light/50"
            }`}
          >
            {occasion.label}
          </Button>
        ))}
      </div>
      {pending && (
        <div role="status" className="mt-4 space-y-3 rounded-xl border border-elfGreen/25 bg-white p-3 text-sm">
          <p className="font-semibold text-foreground">Review the {pending.label.toLowerCase()} starter</p>
          <p className="leading-relaxed text-muted-foreground">{pending.instruction}</p>
          <p className="text-xs text-muted-foreground">Applying this starter replaces the special details below. Your child&apos;s and elf&apos;s names stay saved.</p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={onApply}
              disabled={disabled}
              className="min-h-11 rounded-xl bg-elfGreen-ink text-white hover:bg-[#1c432d] focus-visible:ring-slate-700"
            >
              Use this starter
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={disabled}
              className="min-h-11 rounded-xl focus-visible:ring-slate-700"
            >
              Keep my details
            </Button>
          </div>
        </div>
      )}
    </fieldset>
  );
}
